import { pipe, map, zipWithIndex, reduce, values } from '@fxts/core';
import { AllPoolType } from '@services/MarppleShop/shared/database/db.ts';

abstract class FundingListSortStrategy {
  protected static key: string;

  public static build(_: AllPoolType) {}
}

/**
 * 펀딩 진행중 정렬 전략
 */
class FundingInProgressSortStrategy extends FundingListSortStrategy {
  public static override readonly key = FundingInProgressSortStrategy.toString();

  /**
   * 디폴트 정렬
   */
  public static override build(db: AllPoolType): string {
    return db.SQL`
      CASE 
        WHEN (p.sell_start_at IS NULL OR p.sell_start_at <= NOW()) 
          AND (p.sell_end_at IS NULL OR p.sell_end_at > NOW())
          AND (p.quantity = 0 OR (p.quantity - p.order_count) > 0)
          AND p.is_force_out_of_stock = FALSE
          AND NOT (p.is_force_out_of_stock OR (COALESCE(spo_sum.sum_of_quantity, 1) < 1))
        THEN
          CASE WHEN p.sell_end_at IS NULL THEN 2 ELSE 1 END
        ELSE 999
      END ASC,
      CASE 
        WHEN (p.sell_start_at IS NULL OR p.sell_start_at <= NOW()) 
          AND (p.sell_end_at IS NULL OR p.sell_end_at > NOW())
          AND (p.quantity = 0 OR (p.quantity - p.order_count) > 0)
          AND p.is_force_out_of_stock = FALSE
          AND NOT (p.is_force_out_of_stock OR (COALESCE(spo_sum.sum_of_quantity, 1) < 1))
        THEN p.sell_end_at
        ELSE '9999-12-31'::timestamp
      END ASC
    `;
  }

  // 마감 임박순 정렬
  public static buildDeadlineSort(db: AllPoolType): string {
    return db.SQL`
      CASE 
        WHEN p.sell_start_at <= NOW() 
          AND (p.sell_end_at IS NULL OR p.sell_end_at > NOW())
          AND (p.quantity - p.order_count) > 0 
          AND p.is_force_out_of_stock = FALSE
        THEN
          CASE WHEN p.sell_end_at IS NULL THEN 2 ELSE 1 END
        ELSE 999
      END ASC,
      CASE 
        WHEN p.sell_start_at <= NOW() 
          AND (p.sell_end_at IS NULL OR p.sell_end_at > NOW())
          AND (p.quantity - p.order_count) > 0 
          AND p.is_force_out_of_stock = FALSE
        THEN p.sell_end_at
        ELSE '9999-12-31'::timestamp
      END ASC
    `;
  }

  // 금액 달성률순 정렬
  public static buildAchievementSort(db: AllPoolType): string {
    return db.SQL`
      CASE 
        WHEN p.sell_start_at <= NOW() 
          AND (p.sell_end_at IS NULL OR p.sell_end_at > NOW())
          AND (p.quantity - p.order_count) > 0 
          AND p.is_force_out_of_stock = FALSE
        THEN (COALESCE(total_amount, 0) / NULLIF(mpmcv.value_numeric, 0))
        ELSE 0
      END DESC
    `;
  }
}

/**
 * 펀딩 출시예정 정렬 전략
 */
class FundingComingSoonSortStrategy extends FundingListSortStrategy {
  public static override readonly key = FundingComingSoonSortStrategy.toString();

  /**
   * 디폴트 정렬
   */
  public static override build(db: AllPoolType): string {
    return db.SQL`
      CASE 
        WHEN p.sell_start_at > NOW() THEN p.sell_start_at
        ELSE '9999-12-31'::timestamp
      END ASC
    `;
  }

  // 출시 임박순 정렬
  public static buildLaunchSort(db: AllPoolType): string {
    return db.SQL`
      CASE 
        WHEN p.sell_start_at > NOW() THEN p.sell_start_at
        ELSE '9999-12-31'::timestamp
      END ASC
    `;
  }

  // 최신 등록순 정렬
  public static buildLatestSort(db: AllPoolType): string {
    return db.SQL`
      CASE 
        WHEN p.sell_start_at > NOW() THEN mpmcv.created_at
        ELSE '1900-01-01'::timestamp
      END DESC
    `;
  }
}

/**
 * 펀딩 종료 정렬 전략
 */
class FundingEndSortStrategy extends FundingListSortStrategy {
  public static override readonly key = FundingEndSortStrategy.toString();

  /**
   * 디폴트 정렬
   */
  public static override build(db: AllPoolType): string {
    return db.SQL`
      CASE 
        WHEN ((p.sell_start_at IS NULL OR p.sell_start_at <= NOW()) AND p.sell_end_at IS NOT NULL AND p.sell_end_at <= NOW())
        THEN p.sell_end_at
        WHEN ((p.sell_start_at IS NULL OR p.sell_start_at <= NOW()) 
          AND (p.sell_end_at IS NULL OR p.sell_end_at > NOW())
          AND p.quantity > 0 AND (p.quantity - p.order_count) <= 0)
        THEN COALESCE(p.sell_end_at, NOW())
        WHEN ((p.sell_start_at IS NULL OR p.sell_start_at <= NOW()) 
          AND (p.sell_end_at IS NULL OR p.sell_end_at > NOW())
          AND (p.is_force_out_of_stock OR (COALESCE(spo_sum.sum_of_quantity, 1) < 1)))
        THEN COALESCE(p.sell_end_at, NOW())
        ELSE '1900-01-01'::timestamp
      END DESC
    `;
  }

  // 최신 종료순 정렬
  public static buildLatestEndSort(db: AllPoolType): string {
    return db.SQL`
      CASE 
        WHEN ((p.sell_start_at IS NULL OR p.sell_start_at <= NOW()) AND p.sell_end_at IS NOT NULL AND p.sell_end_at <= NOW())
        THEN p.sell_end_at
        WHEN ((p.sell_start_at IS NULL OR p.sell_start_at <= NOW()) 
          AND (p.sell_end_at IS NULL OR p.sell_end_at > NOW())
          AND p.quantity > 0 AND (p.quantity - p.order_count) <= 0)
        THEN COALESCE(p.sell_end_at, NOW())
        WHEN ((p.sell_start_at IS NULL OR p.sell_start_at <= NOW()) 
          AND (p.sell_end_at IS NULL OR p.sell_end_at > NOW())
          AND (p.is_force_out_of_stock OR (COALESCE(spo_sum.sum_of_quantity, 1) < 1)))
        THEN COALESCE(p.sell_end_at, NOW())
        ELSE '1900-01-01'::timestamp
      END DESC
    `;
  }

  // 성공률순 정렬
  public static buildSuccessRateSort(db: AllPoolType): string {
    return db.SQL`
      CASE 
        WHEN ((p.sell_start_at IS NULL OR p.sell_start_at <= NOW()) AND p.sell_end_at IS NOT NULL AND p.sell_end_at <= NOW())
          OR ((p.sell_start_at IS NULL OR p.sell_start_at <= NOW()) 
            AND (p.sell_end_at IS NULL OR p.sell_end_at > NOW())
            AND p.quantity > 0 AND (p.quantity - p.order_count) <= 0)
          OR ((p.sell_start_at IS NULL OR p.sell_start_at <= NOW()) 
            AND (p.sell_end_at IS NULL OR p.sell_end_at > NOW())
            AND (p.is_force_out_of_stock OR (COALESCE(spo_sum.sum_of_quantity, 1) < 1)))
        THEN 
          CASE WHEN mpmcv.value_numeric > 0 
            THEN (COALESCE(total_amount, 0) / mpmcv.value_numeric)
            ELSE 0
          END
        ELSE 0
      END DESC
    `;
  }
}

/**
 * 펀딩 정렬 전략 컴포짓 클래스
 */
export class FundingCompositeSortStrategy {
  private constructor(
    private db: AllPoolType,
    private strategies: (
      | typeof FundingCompositeSortStrategy.InProgress
      | typeof FundingCompositeSortStrategy.ComingSoon
      | typeof FundingCompositeSortStrategy.End
    )[],
    private sort: Record<string, typeof FundingListSortStrategy.build>,
  ) {
    if (!this.sort.in_progress) this.sort.in_progress = FundingCompositeSortStrategy.InProgress.build;
    if (!this.sort.coming_soon) this.sort.coming_soon = FundingCompositeSortStrategy.ComingSoon.build;
    if (!this.sort.end) this.sort.end = FundingCompositeSortStrategy.End.build;
  }

  build(): ReturnType<typeof this.db.SQL> {
    // 상태별 우선순위 CASE 문 생성
    const status_sort_sql = pipe(
      this.strategies,
      zipWithIndex,
      map(([index, strategy]) => {
        const priority = index + 1;

        switch (strategy.key) {
          case FundingCompositeSortStrategy.InProgress.toString():
            return this.db.SQL`WHEN (p.sell_start_at IS NULL OR p.sell_start_at <= NOW()) 
                AND (p.sell_end_at IS NULL OR p.sell_end_at > NOW())
                AND (p.quantity = 0 OR (p.quantity - p.order_count) > 0)
                AND NOT (p.is_force_out_of_stock OR (COALESCE(spo_sum.sum_of_quantity, 1) < 1))
              THEN ${priority}`;
          case FundingCompositeSortStrategy.ComingSoon.toString():
            return this.db.SQL`WHEN p.sell_start_at IS NOT NULL AND p.sell_start_at > NOW() THEN ${priority}`;
          case FundingCompositeSortStrategy.End.toString():
            return this.db.SQL`WHEN ((p.sell_start_at IS NULL OR p.sell_start_at <= NOW()) 
              AND p.sell_end_at IS NOT NULL AND p.sell_end_at <= NOW())
              OR ((p.sell_start_at IS NULL OR p.sell_start_at <= NOW()) 
                AND (p.sell_end_at IS NULL OR p.sell_end_at > NOW())
                AND p.quantity > 0 AND (p.quantity - p.order_count) <= 0)
              OR ((p.sell_start_at IS NULL OR p.sell_start_at <= NOW()) 
                AND (p.sell_end_at IS NULL OR p.sell_end_at > NOW())
                AND (p.is_force_out_of_stock OR (COALESCE(spo_sum.sum_of_quantity, 1) < 1)))
            THEN ${priority}`;
          default:
            return this.db.SQL``;
        }
      }),
      reduce((a, b) => this.db.SQL`${a} ${b}`),
      (sql) => this.db.SQL`${sql} ELSE 999`,
    );

    // 각 상태별 세부 정렬 조건
    const status_detail_sort_sql = pipe(
      this.sort,
      values,
      map((build) => build(this.db)),
      reduce((a, b) => this.db.SQL`${a}, ${b}`),
    );

    return this.db.SQL`
      ORDER BY 
        CASE 
          ${status_sort_sql}
        END ASC,
        ${status_detail_sort_sql}
    `;
  }

  static InProgress = FundingInProgressSortStrategy;
  static ComingSoon = FundingComingSoonSortStrategy;
  static End = FundingEndSortStrategy;

  static create(
    db: AllPoolType,
    strategies: (
      | typeof FundingCompositeSortStrategy.InProgress
      | typeof FundingCompositeSortStrategy.ComingSoon
      | typeof FundingCompositeSortStrategy.End
    )[],
    sort: Record<string, typeof FundingListSortStrategy.build> = {},
  ) {
    return new FundingCompositeSortStrategy(db, strategies, sort);
  }
}

export type FundingSortStrategies = (
  | typeof FundingCompositeSortStrategy.InProgress
  | typeof FundingCompositeSortStrategy.ComingSoon
  | typeof FundingCompositeSortStrategy.End
)[];
export type FundingSortOptions = Partial<{
  in_progress: typeof FundingListSortStrategy.build;
  coming_soon: typeof FundingListSortStrategy.build;
  end: typeof FundingListSortStrategy.build;
}>;
